"use client";

import { createContext, useMemo, type ReactNode } from "react";
import { type Locale } from "@/config/locale";
import { classifyMismatch, type MismatchDecision } from "@/lib/geo/mismatch-detection";

export interface LocaleChannelContextValue {
	currentLocale: Locale;
	/** The gate's enabled set, resolved server-side and passed down as data. */
	enabledLocales: readonly Locale[];
	currentChannel: string;
	/** ISO 3166-1 alpha-2 from CF-IPCountry, or null if unavailable. */
	detectedCountry: string | null;
	/** Whether a geo_pref cookie already exists for this browser. */
	hasGeoPrefCookie: boolean;
	/** Whether the mismatch banner has been dismissed for this {country}-{channel}. */
	mismatchDismissed: boolean;
	/** Computed once via classifyMismatch on the server-passed values. */
	mismatch: MismatchDecision;
}

export const LocaleChannelContext = createContext<LocaleChannelContextValue | null>(null);

export interface LocaleChannelProviderProps {
	currentLocale: Locale;
	/** Resolved by the server parent — a client-side read would resolve the environment differently. */
	enabledLocales: readonly Locale[];
	currentChannel: string;
	detectedCountry: string | null;
	hasGeoPrefCookie: boolean;
	mismatchDismissed: boolean;
	children: ReactNode;
}

export function LocaleChannelProvider({
	currentLocale,
	enabledLocales,
	currentChannel,
	detectedCountry,
	hasGeoPrefCookie,
	mismatchDismissed,
	children,
}: LocaleChannelProviderProps) {
	const value = useMemo<LocaleChannelContextValue>(
		() => ({
			currentLocale,
			enabledLocales,
			currentChannel,
			detectedCountry,
			hasGeoPrefCookie,
			mismatchDismissed,
			mismatch: classifyMismatch({ currentChannel, detectedCountry }),
		}),
		[currentLocale, enabledLocales, currentChannel, detectedCountry, hasGeoPrefCookie, mismatchDismissed],
	);

	return <LocaleChannelContext.Provider value={value}>{children}</LocaleChannelContext.Provider>;
}
